/**
 * OAuth entry point for visitors arriving from the marketing landing at
 * `/login?provider=…`.
 *
 * Lives outside login.vue on purpose. This is not part of the login form — the
 * page renders nothing for these visitors, it only records the ad session's
 * cookies on this domain and hands them over to the backend. It is also the
 * half that welcome.vue would need if the landing ever points there, and
 * login.vue is already long enough that burying an unrelated redirect flow in
 * it makes both harder to follow.
 */
import type { LocationQuery, LocationQueryValue } from 'vue-router';
import type { SocialProvider } from '@store/api/interfaces';
import { usePaymentFlowStore } from '@store/paymentFlow';
import i18n from '@/i18n';
import { gtmClientInfo, gtmSessionActivity } from '@composables/gtm';

const SUPPORTED_PROVIDERS: SocialProvider[] = ['telegram', 'google'];

// How long to let gtag finish writing its cookies before handing the visitor
// over to the API host. The whole point of entering OAuth from this domain is
// that the ad session is recorded here first — but a blocked or slow GTM must
// never hold the login hostage, so the wait is capped and failure is silent.
const GA_COOKIE_WAIT_MS = 1500;
const GA_COOKIE_POLL_MS = 50;

const first = (value: LocationQueryValue | LocationQueryValue[] | undefined): string | undefined => {
	const raw = Array.isArray(value) ? value[0] : value;
	return raw ? String(raw) : undefined;
};

/**
 * Validate `?provider=` against the providers we actually support. Anything
 * else returns null and the login page renders its normal form — an unknown
 * value must not turn into a redirect to an arbitrary URL.
 */
export const providerFromQuery = (
	value: LocationQueryValue | LocationQueryValue[] | undefined,
): SocialProvider | null => {
	const provider = first(value);
	return SUPPORTED_PROVIDERS.includes(provider as SocialProvider)
		? (provider as SocialProvider)
		: null;
};

/** Does the address carry a cross-domain linker parameter to unpack? */
const hasLinkerParam = (): boolean => {
	try {
		return new URLSearchParams(window.location.search).has('_gl');
	} catch {
		return false;
	}
};

/**
 * Resolve once this domain's GA identity is settled, or once the cap expires.
 *
 * With `_gl` in the address, neither an existing `_ga` cookie nor a registered
 * container proves anything. A returning visitor already has the cookie from an
 * earlier visit, and the container registers itself BEFORE GA4 does its work —
 * measured on production, with gtm.js already cached:
 *
 *     537 ms  gtm.js executed         → container object registered
 *     638 ms  first hit to /g/collect → cookies written, `_gl` accepted
 *
 * Leaving inside that ~100 ms window (wider on a cold load) would send the
 * visitor to the API host still carrying their OLD client_id — losing the very
 * ad session this entry point exists to preserve.
 *
 * What does prove it is a CHANGE in the session cookie's `$t` mark: it is
 * refreshed on every hit, and a hit only leaves after gtag has accepted the
 * incoming `_gl`. Unlike client_id, `$t` moves even when the linker carried an
 * identity we already had — so a correct visit never waits out the cap.
 *
 * Without `_gl` there is nothing to unpack and an existing cookie is answer
 * enough, so nobody is made to wait.
 */
const waitForGaIdentity = (): Promise<void> => {
	const needsLinkerPickup = hasLinkerParam();
	// Snapshot taken before the wait; everything after it answers "did a hit
	// land on THIS page load".
	const activityBefore = needsLinkerPickup ? gtmSessionActivity() : undefined;

	const hitLanded = (): boolean => {
		const now = gtmSessionActivity();
		if (now === undefined) return false;
		// No session beforehand means the first hit is the one awaited.
		return activityBefore === undefined || now > activityBefore;
	};

	return new Promise((resolve) => {
		const startedAt = Date.now();

		const check = () => {
			const settled = needsLinkerPickup ? hitLanded() : !!gtmClientInfo().clientId;

			// The cap resolves unconditionally: a blocked or slow container must
			// never hold the sign-in hostage.
			if (settled || Date.now() - startedAt >= GA_COOKIE_WAIT_MS) {
				resolve();
				return;
			}
			setTimeout(check, GA_COOKIE_POLL_MS);
		};

		check();
	});
};

/**
 * Record the ad session on this domain, then hand the visitor to the backend.
 *
 * The landing used to send them straight to the API host. That host is a
 * different eTLD+1 and is not in the cross-domain linker's list, so the ad
 * session was dropped at the very first hop and the eventual purchase came
 * back unattributed. Starting here means the cookies are written on
 * step-vpn.cloud first, and every later hop stays within it.
 */
export const startProviderAuth = async (
	provider: SocialProvider,
	query: LocationQuery,
): Promise<void> => {
	// Carry the landing's context across the OAuth round-trip, the same way the
	// welcome page does — the return lands back on /login, not here.
	const promo = first(query.promo);
	if (promo) {
		usePaymentFlowStore().promoCode = promo;
	}

	if (first(query.redirect) === 'payment') {
		const target = promo ? `/payment?promo=${encodeURIComponent(promo)}` : '/payment';
		try {
			sessionStorage.setItem('welcome-redirect', target);
		} catch {
			// Private mode without storage — the user just lands on Home.
		}
	}

	await waitForGaIdentity();

	const lang = first(query.lang) || i18n.global.locale.value;
	const url = new URL(`${import.meta.env.VITE_API_BASE}/auth/${provider}/redirect`);
	if (lang) url.searchParams.set('lang', lang);

	// Top-level navigation on purpose: a popup would move the ad session into a
	// separate context and undo everything above.
	window.location.href = url.toString();
};
